import { FC, useMemo, useState, useEffect } from "react";
import clsx from "classnames";
import { App } from "@capacitor/app";
import { useQuery } from "@apollo/client/react/hooks/useQuery";
import { Query, featuredCardsAaaGql } from "@onmo/graphql/graphql";
import { useStyles } from "./ConsoleDesktopContent.style";
import { ConsoleCardStatus } from "../ConsoleCardStatus";
import { ConsoleDeviceCard } from "../ConsoleDeviceCard/ConsoleDeviceCard";
import { ConsoleActivityCard } from "../ConsoleActivityCard/ConsoleActivityCard";
import { ConsoleServiceCard } from "../ConsoleServiceCard";
import { FeaturedConsole } from "../FeaturedConsole";
import PairControllerPopup from "../PairControllerPopup/PairControllerPopup";
import DownloadPopup from "../DownloadPopup/DownloadPopup";
import {
  ConsoleDesktopId,
  FeatureDesktopGroup,
} from "../../../constants/console";
import { getFeatureCardsMap } from "../../../models/console";
import ActivateQrPopup from "../ActivateQrPopup/ActivateQrPopup";
import { AnalyticsGA4 } from "../../../models/analyticsGA4";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { Dialog } from "../../core/Dialog/Dialog";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import {
  isTV,
  isAndroidAppTv,
  isTizen,
  isWebos,
} from "../../../utils/platform";

interface ConsoleDesktopContentProps {
  totalTimePlayed: number;
  totalPlayedGames: number;
}

const ConsoleDesktopContent: FC<ConsoleDesktopContentProps> = ({
  totalTimePlayed,
  totalPlayedGames,
}) => {
  const classes = useStyles();
  const [openPairController, setOpenPairController] = useState(false);
  const [openDownloadPopup, setOpenDownloadPopup] = useState(false);
  const [activateQRLink, setActivateQRLink] = useState("");
  const [isExitApp, setIsExitApp] = useState(false);
  const { data: featuredCardsData } = useQuery<Query>(featuredCardsAaaGql, {
    variables: { input: { group: FeatureDesktopGroup } },
  });
  const { setCallback, cleanup, setActiveElement } = useGamepadContext();

  const featureCards = useMemo(
    () => getFeatureCardsMap(featuredCardsData, ConsoleDesktopId),
    [featuredCardsData],
  );

  const handleOpenPairController = () => {
    setOpenPairController(true);
    AnalyticsGA4.homepageAction("controller");
  };

  const onExitApp = () => {
    if (isAndroidAppTv) {
      App.exitApp();
    }
    if (isTizen) {
      window?.tizen?.application?.getCurrentApplication?.()?.exit?.();
    }
    if (isWebos) {
      window?.close();
    }
  };

  useEffect(() => {
    if (!isTV) return;
    setCallback({
      onBackAction: (activeElement) => {
        const dataGamepad = activeElement?.getAttribute("data-gamepad");
        const activeMenu = dataGamepad?.startsWith("menu-");
        const menuButton = document.querySelector(
          '[data-gamepad="menu-/"]',
        ) as HTMLElement;
        if (activeMenu) {
          setIsExitApp(true);
        } else {
          setActiveElement(menuButton);
        }
      },
    });
    return () => {
      cleanup();
    };
  }, [setCallback, cleanup, setActiveElement]);

  return (
    <>
      <ActivateQrPopup
        onClose={() => setActivateQRLink("")}
        activateLink={activateQRLink}
      />
      <PairControllerPopup
        open={openPairController}
        onClose={() => setOpenPairController(false)}
      />
      <DownloadPopup
        open={openDownloadPopup}
        onClose={() => setOpenDownloadPopup(false)}
      />
      <div className={classes.contentRoot}>
        <div
          className={clsx(classes.consoleContent, classes.consoleContentLeft)}
        >
          <ConsoleCardStatus
            className={classes.consoleCardDesktop}
            setActivateQRLink={setActivateQRLink}
          />
          <ConsoleDeviceCard
            onClick={handleOpenPairController}
            className={classes.consoleCardDesktop}
          />
          <ConsoleActivityCard
            totalTimePlayed={totalTimePlayed}
            totalPlayedGames={totalPlayedGames}
            className={clsx(classes.consoleCardDesktop, classes.activityCard)}
          />
        </div>
        <div className={classes.consoleContent}>
          {featureCards?.["byog_card"] && (
            <ConsoleServiceCard
              type="install"
              data={featureCards["byog_card"]}
              className={classes.serviceCard}
            />
          )}
          {featureCards?.["play_anywhere_card"] && (
            <ConsoleServiceCard
              type="download"
              onClick={() => setOpenDownloadPopup(true)}
              data={featureCards["play_anywhere_card"]}
              className={classes.serviceCard}
            />
          )}
        </div>
        {featureCards?.["featured_card"] && (
          <FeaturedConsole
            className={clsx(classes.consoleContent, classes.featuredCard)}
            data={featureCards["featured_card"]}
          />
        )}
      </div>
      <Dialog
        mainAction={{
          text: "Exit App",
          onClick: onExitApp,
          id: "exit",
        }}
        secondAction={{
          text: "Cancel",
          onClick: () => setIsExitApp(false),
          id: "close-dialog",
          "data-button-sound": "back",
        }}
        open={isExitApp}
      >
        <div className={classes.content}>
          <Typography
            variant={TypographyVariant.HeadingH3Bold}
            className={classes.title}
          >
            Power Off
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB3Medium}
            className={classes.description}
          >
            This will close ONMO+ and return you to the TV home screen.
          </Typography>
        </div>
      </Dialog>
    </>
  );
};

export default ConsoleDesktopContent;
